import React, { useEffect, useRef, useState } from "react";
import { MapContainer as MapContainerBase, TileLayer, Marker as MarkerBase, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import styles from "./MapWrapper.module.scss";
import { pickupMarkerIcon, dropoffMarkerIcon } from "./mapMarkers";
import { isWaypointMarkerId } from "./routeMarkers";
import {
  BELGRADE_CENTER,
  BOOKING_PANEL_GUTTER_PX,
  getBookingPanelOffsetPx,
  getVisibleMapCenter,
} from "./orderFormConstants";
import { MapMarker, MapMarkerId, RouteCoords } from "./types";
import pinWaypointUrl from "../../static/ride-order/pin-waypoint.svg";

const MapContainer = MapContainerBase as React.ComponentType<any>;
const Marker = MarkerBase as React.ComponentType<any>;

const MAP_DEFAULT_CENTER: [number, number] = [BELGRADE_CENTER.lat, BELGRADE_CENTER.lng];
const DEFAULT_ZOOM = 13;
const SINGLE_MARKER_ZOOM = 14;
const PIN_BOUNDS_PADDING: [number, number] = [56, 56];

const WAYPOINT_PIN_SIZE: [number, number] = [56, 56];
const WAYPOINT_PIN_ANCHOR: [number, number] = [28, 56];
const waypointIconCache = new Map<number, L.DivIcon>();

function getWaypointIcon(index: number): L.DivIcon {
  const cached = waypointIconCache.get(index);
  if (cached) return cached;

  const icon = L.divIcon({
    className: styles.waypointMarkerIcon,
    html: `
      <div class="${styles.waypointMarkerInner}">
        <img class="${styles.waypointMarkerPin}" src="${pinWaypointUrl}" width="56" height="56" alt="" aria-hidden="true" />
        <span class="${styles.waypointMarkerLabel}" aria-hidden="true">${index}</span>
      </div>
    `,
    iconSize: WAYPOINT_PIN_SIZE,
    iconAnchor: WAYPOINT_PIN_ANCHOR,
  });

  waypointIconCache.set(index, icon);
  return icon;
}

function getMarkerIcon(marker: MapMarker) {
  if (isWaypointMarkerId(marker.id)) {
    return getWaypointIcon(marker.waypointIndex ?? 1);
  }
  return marker.id === "pickup" ? pickupMarkerIcon : dropoffMarkerIcon;
}

function getFitBoundsPadding(viewportWidth: number) {
  const [pinX, pinY] = PIN_BOUNDS_PADDING;
  const panelOffset = getBookingPanelOffsetPx(viewportWidth);

  if (panelOffset === 0) {
    return {
      paddingTopLeft: [pinX, pinY] as [number, number],
      paddingBottomRight: [pinX, pinY] as [number, number],
    };
  }

  return {
    paddingTopLeft: [panelOffset + pinX, pinY] as [number, number],
    paddingBottomRight: [BOOKING_PANEL_GUTTER_PX + pinX, pinY] as [number, number],
  };
}

function markersMatch(a: MapMarker[], b: MapMarker[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (marker, index) => b[index]?.id === marker.id && b[index]?.lat === marker.lat && b[index]?.lng === marker.lng,
  );
}

const MapViewController: React.FC<{ markers: MapMarker[] }> = ({ markers }) => {
  const map = useMap();
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  const prevMarkersRef = useRef<MapMarker[]>([]);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    map.invalidateSize();
  }, [map, viewportWidth]);

  useEffect(() => {
    if (markersMatch(prevMarkersRef.current, markers)) return;

    prevMarkersRef.current = markers;

    if (markers.length === 0) {
      if (!hasInitializedRef.current) {
        map.setView(MAP_DEFAULT_CENTER, DEFAULT_ZOOM);
        hasInitializedRef.current = true;
      }
      return;
    }

    hasInitializedRef.current = true;
    const panelOffset = getBookingPanelOffsetPx(viewportWidth);

    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], SINGLE_MARKER_ZOOM);
      if (panelOffset > 0) {
        map.panBy([-panelOffset / 2, 0], { animate: false });
      }
      return;
    }

    const bounds = L.latLngBounds(markers.map((marker) => [marker.lat, marker.lng] as [number, number]));

    map.fitBounds(bounds, {
      ...getFitBoundsPadding(viewportWidth),
      maxZoom: 14,
    });
  }, [map, markers, viewportWidth]);

  return null;
};

const MapCenterGetter: React.FC<{
  onReady: (getCenter: () => RouteCoords) => void;
}> = ({ onReady }) => {
  const map = useMap();

  useEffect(() => {
    onReady(() => getVisibleMapCenter(map));
  }, [map, onReady]);

  return null;
};

interface MapWrapperProps {
  markers: MapMarker[];
  onMarkerDrag?: (markerId: MapMarkerId, coords: RouteCoords) => void;
  onMapCenterGetterReady?: (getCenter: () => RouteCoords) => void;
}

const MapWrapper: React.FC<MapWrapperProps> = ({ markers, onMarkerDrag, onMapCenterGetterReady }) => (
  <div className={styles.mapContainer}>
    <MapContainer
      zoomControl={false}
      attributionControl={false}
      center={MAP_DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      scrollWheelZoom
      className={styles.mapWrap}>
      <TileLayer url="https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=cb1_2g08_1_e9d474a7cdba96691b28ac2c" />
      <MapViewController markers={markers} />
      {onMapCenterGetterReady && <MapCenterGetter onReady={onMapCenterGetterReady} />}

      {markers.map((marker) => (
        <Marker
          key={marker.id}
          position={[marker.lat, marker.lng]}
          icon={getMarkerIcon(marker)}
          draggable={Boolean(onMarkerDrag)}
          eventHandlers={
            onMarkerDrag
              ? {
                  dragend: (event: { target: L.Marker }) => {
                    const { lat, lng } = event.target.getLatLng();
                    onMarkerDrag(marker.id, { lat, lng });
                  },
                }
              : undefined
          }
        />
      ))}
    </MapContainer>
  </div>
);

export default MapWrapper;
